/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Directive, Input } from '@angular/core';
import { DND_GRID_ENGINE, DndDragSource, DndManager, GridDndDropTarget } from '@gh/core-ui';
import { arrayify } from '@gh/core-util';
import { concat$, first$, map$ } from '@gh/rx/operators';
import {
  DragAndDropService,
  DraggingEvent,
  DropTarget,
  Events,
  GridOptions,
  RowDragEnterEvent,
} from 'ag-grid-community';
import { create } from 'lodash';
import { Subscription } from 'rxjs';
import { GRID_FEATURE, GridFeature, GridFeatureInitParams, GridFeatureReadyParams } from '../feature.interfaces';

/**
 * This grid feature makes grid component suitable for <code>DragSource</code> role in heteragenous DnD operations.
 */
@Directive({
  selector: '[ghGridDragSource]',
  providers: [{
    provide: GRID_FEATURE,
    useExisting: GridDragSourceDirective,
    multi: true,
  }],
  exportAs: 'ghGridDragSource',
})
export class GridDragSourceDirective implements GridFeature {
  @Input('ghGridDragSource') selector: string;

  private gridDnd: DragAndDropService;

  private dragSource: DndDragSource;
  private dropTargetSubscription: Subscription;

  private dragging: boolean = false;

  constructor(private dndManager: DndManager) {

  }

  initGrid(params: GridFeatureInitParams): void {
    // nothing to do
  }

  enhanceGridOptions(options: GridOptions): void {
    // nothing to do
  }

  onGridReady(params: GridFeatureReadyParams): void {
    this.gridDnd = params.sharedBeans.dragAndDropService;

    // create new drag source
    this.dragSource = this.dndManager.createDragSource(this.selector, DND_GRID_ENGINE);

    // listene for drop targets
    const dndSubscription = this.dndManager.listenDropTargets(this.selector, DND_GRID_ENGINE);
    this.dropTargetSubscription = dndSubscription.targets.pipe(
      first$(),
      concat$(dndSubscription.targetAdded.pipe(map$(arrayify))))
      .subscribe((targets: GridDndDropTarget[]) =>
        targets.forEach((target) => this.gridDnd.addDropTarget(this.proxyGridDropTarget(target.gridDropTarget))));

    // unfortunately currently ag-grid does not allow to remove DropTargets
    // as a solution we can proxy actual drop target and reset original target when it should be removed
    // dndSubscription.targetRemoved
    //   .subscribe((target) => this.gridDnd.removeDropTarget(target.gridDropTarget));

    params.api.addEventListener(Events.EVENT_ROW_DRAG_ENTER, this.onRowDragEnter.bind(this));
    params.api.addEventListener(Events.EVENT_ROW_DRAG_END, this.onRowDragEnd.bind(this));
  }

  onDestroy(): void {
    if (this.dropTargetSubscription) {
      this.dropTargetSubscription.unsubscribe();
    }
    this.dndManager.removeDragSource(this.dragSource);
  }

  private onRowDragEnter(event: RowDragEnterEvent): void {
    if (!this.dragging) {
      this.dragSource.startDrag(event.node.data);
    }

    this.dragging = true;
  }

  private onRowDragEnd(): void {
    this.dragging = false;
    this.dragSource.stopDrag();
  }

  /**
   * This method proxy {@link DropTarget}.
   * We have to proxy drop target to delegate onRowDragEnd event.
   * Grid does not fire onRowDragEnd event if it has occurred outside of grid.
   */
  private proxyGridDropTarget(dropTarget: DropTarget): DropTarget {
    return create(dropTarget, {
      onDragStop: (event: DraggingEvent) => {
        if (dropTarget.onDragStop) {
          dropTarget.onDragStop(event);
        }
        this.onRowDragEnd();
      },
    });
  }
}
